import React, { type FormEvent, useCallback } from 'react';

import { useNavigate } from 'react-router';

import { useAppDispatch } from 'dux/app/hooks';

import { Button, type ButtonProps } from 'Components/LegacyButton';

import { productsCategories } from 'constants/products';

import { trackPreselect } from 'dux/tracking/actions';
import { useProductDetailsCTA as useProductDetailsCTAFn } from 'dux/userFlows/useProductDetailsCTA';

type ProductCTAProps = {
  dataFrom?: string;
  render?:
    | null
    | React.ReactNode
    | ((
        renderArgs: ReturnType<typeof useProductDetailsCTAFn> & {
          onClick: (e: any, executeCallback: boolean) => void;
        },
      ) => React.ReactNode);
  label?: string;
  /**
   * @deprecated
   * please use variant instead.
   */
  theme?: 'roundedBlack' | 'vert' | 'sorbetGreenOnHover' | 'sorbet';
  variant?: 'roundedBlack' | 'vert' | 'sorbetGreenOnHover' | 'sorbet';
  slug?: string;
  productCategory?: string;
  useProductDetailsCTA?: typeof useProductDetailsCTAFn;
  customOnClick?: (arg0: string) => void;
};

type ProductCTAPropsWithButtonProps = ProductCTAProps &
  Omit<ButtonProps<'button'>, keyof ProductCTAProps | 'children'>;

export const ProductCTA = ({
  dataFrom = 'consultation-cta',
  render,
  label,
  slug,
  /** @deprecated
   * Please use variant instead.
   */
  theme = 'sorbet',
  variant = theme,
  useProductDetailsCTA = useProductDetailsCTAFn,
  productCategory = productsCategories.HAIRCARE,
  customOnClick,
  ...props
}: ProductCTAPropsWithButtonProps) => {
  const dispatch = useAppDispatch();
  const params = useProductDetailsCTA({ slug, productCategory });
  const navigate = useNavigate();
  const recommend = useCallback(
    (theSlug: string, { executeCallback = true }) => {
      dispatch(
        /* @ts-expect-error - trackPreselect not ported to TS yet */
        trackPreselect({
          productSlug: theSlug,
          locationId: `pdp-${theSlug}`,
          // We want to make sure that the breadcrumbs call is finished before starting a cart API call
          onSuccess: executeCallback ? () => navigate(params.to) : () => {},
          productCategory,
        }),
      );
    },
    [productCategory, navigate],
  );

  const onClick = (e: FormEvent<HTMLFormElement>, executeCallback = true) => {
    if (customOnClick) {
      customOnClick(params.to);
    } else if (slug) {
      recommend(slug, { executeCallback });
    }
    if (!executeCallback) {
      navigate(params.to);
    }
    e.preventDefault();
  };

  if (render) {
    return typeof render === 'function' ? render({ ...params, onClick }) : render;
  }

  return (
    <form onSubmit={(e) => onClick(e, params.shouldQueueBreadcrumb)}>
      <Button
        data-click={params.dataClick}
        data-from={dataFrom}
        /* @ts-expect-error - isLoading DOES exist but isn't infered correctly for some reasons */
        isLoading={params?.isLoading ?? false}
        type="submit"
        variant={variant ?? theme}
        {...props}
      >
        {label || params.label}
      </Button>
    </form>
  );
};
